import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/app/store/store';
import { posCartBuild } from '../../config/posCart.build';
import StoreSelectionCard from './StoreSelectionCard';
import type { PosCartItem } from '../../domain/models/PosCartItem';
import { DesignsPickedStarIcon, posCartBgSvg } from '@/shared/icons/icons';
import { cn } from '@/shared/utils/cn';

// Customization & PDP feature imports
import CustomizationSheetV2 from '@/features/customization/presentation/components/CustomizationSheetV2';
import { PdpCartFullPopup } from '@/features/pdp/presentation/components/PdpCartFullPopup';
import { useStoreSelections } from '../hooks/useStoreSelections';
import { InfiniteHorizontalScroll } from '@/shared/components/widgets/InfiniteHorizontalScroll';

export interface StoreSelectionsSectionProps {
  items: PosCartItem[];
  pageName: 'Home' | 'PDP';
  onLoadMore?: () => void;
  hasMore?: boolean;
}

export default function StoreSelectionsSection({
  items,
  pageName,
  onLoadMore,
  hasMore,
}: StoreSelectionsSectionProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => posCartBuild(dispatch), [dispatch]);

  const {
    sliderRef,
    selectedProduct,
    isLoadingProductDetail,
    loadedProductDetail,
    isCustomizationOpen,
    isCartFullPopupOpen,
    setIsCartFullPopupOpen,
    isSelectedProductWishlisted,
    handleAddToWishlistFromPopup,
    handleWishlistToggle,
    sheet,
    handleAddToCart,
    handleApplyCustomization,
    handleGoToCart,
    handleProductClick,
    getTitleText,
    cartCount,
    maxCartItemsCount,
  } = useStoreSelections(service, { pageName, onLoadMore, hasMore });

  if (items.length === 0) return null;

  const isPdpLessItems = pageName === 'PDP' && items.length <= 3;

  return (
    <section
      id="store-visit-cart-items-section"
      className="w-full py-3 pr-1 relative"
    >
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(to bottom, #fff, transparent 15%, transparent 85%, #fff), url("${posCartBgSvg}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: isPdpLessItems ? 0.5 : 1,
        }}
      />
      <div className="mx-auto w-full max-w-none relative z-10">
        <div className="flex flex-col items-center justify-center text-center mb-2">
          <h2
            className={cn(
              "nh-section-title nh-section-title--desktop-color px-4 py-3 text-center capitalize leading-none tracking-[0.01em] font-serif text-[#0D2750] flex items-center justify-center gap-3",
              pageName === 'PDP'
                ? "!text-[16px] md:!text-[20px]"
                : "!text-[24px] md:!text-[30px]"
            )}
            style={{
              ['--nh-title-desktop' as string]: '#1e3a5f',
            } as React.CSSProperties}
          >
            <DesignsPickedStarIcon className={cn("shrink-0", pageName === 'PDP' ? "h-4 w-4 md:h-5 md:w-5" : "h-6 w-6 md:h-8 md:w-8")} />
            <span className="font-serif">{getTitleText()}</span>
            <DesignsPickedStarIcon className={cn("shrink-0", pageName === 'PDP' ? "h-4 w-4 md:h-5 md:w-5" : "h-6 w-6 md:h-8 md:w-8")} />
          </h2>
        </div>

        <InfiniteHorizontalScroll
          items={items}
          sliderRef={sliderRef}
          isInfiniteScroll={true}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          alignCenterIfLessOrEqual={pageName === 'PDP' ? 3 : 2}
          renderItem={(item, idx, isAtLeftMost) => (
            <StoreSelectionCard
              key={item.designId}
              product={item}
              onAddToCart={handleAddToCart}
              onGoToCart={handleGoToCart}
              onProductClick={handleProductClick}
              onWishlistToggle={handleWishlistToggle}
              hideCta={pageName === 'PDP'}
              isPdp={pageName === 'PDP'}
              className={idx === 0 ? (isAtLeftMost ? 'ml-[10px] md:ml-[18px]' : 'ml-0') : undefined}
            />
          )}
        />
      </div>

      <CustomizationSheetV2
        open={isCustomizationOpen}
        designName={selectedProduct?.designName || ''}
        draft={sheet.draft}
        parsedOptions={sheet.parsedOptions}
        goldPurities={sheet.goldPurities}
        previewPrice={sheet.previewPrice}
        isPreviewLoading={sheet.isPreviewLoading}
        isPriceAnimating={sheet.isPriceAnimating}
        isApplying={sheet.isApplying || isLoadingProductDetail}
        onClose={sheet.handleCancel}
        onOptionChange={sheet.handleOptionChange}
        onApply={handleApplyCustomization}
        onCancel={sheet.handleCancel}
        product={selectedProduct}
        productDetail={loadedProductDetail}
      />

      <PdpCartFullPopup
        open={isCartFullPopupOpen}
        onClose={() => setIsCartFullPopupOpen(false)}
        isWishlisted={isSelectedProductWishlisted}
        onAddToWishlist={handleAddToWishlistFromPopup}
        cartCount={cartCount}
        maxCartItemsCount={maxCartItemsCount}
      />
    </section>
  );
}
